"use client";

import { Calendar, Compass, Heart, Palette, Sparkles, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const milestones = [{
  number: "01",
  timing: "SCENE START",
  title: "Character Analysis",
  description: "Deconstructing the script to find the psychological core and physical rhythm of the role.",
  icon: Heart
}, {
  number: "02",
  timing: "PRE-PRODUCTION",
  title: "Visual Identity",
  description: "Collaborating with costume and makeup to define the external silhouette and period accuracy.",
  icon: Palette
}, {
  number: "03",
  timing: "REHEARSAL",
  title: "Ensemble Dynamics",
  description: "Building chemistry and spatial awareness with the cast to create authentic human connection.",
  icon: Users
}, {
  number: "04",
  timing: "ON SET",
  title: "Technical Precision",
  description: "Executing the performance within the frame, balancing emotional truth with camera choreography.",
  icon: Compass
}, {
  number: "05",
  timing: "POST-WRAP",
  title: "ADR & Refinement",
  description: "Polishing the vocal performance and ensuring every nuance translates to the final edit.",
  icon: Calendar
}, {
  number: "06",
  timing: "PREMIERE",
  title: "The Final Frame",
  description: "The culmination of the craft, where the character is finally released to the audience.",
  icon: Sparkles
}];
export default function CareerMilestones() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const sectionRef = useRef<HTMLElement | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const [fillProgress, setFillProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const el = timelineRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const total = rect.height + viewportH;
      const scrolled = viewportH - rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / total));
      setFillProgress(progress);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  return <section data-section-id="429" id="career-milestones" ref={sectionRef} className="bg-background text-foreground relative overflow-hidden py-24 md:py-40">
      <svg className="pointer-events-none absolute top-0 left-0 w-40 md:w-64 h-40 md:h-64 text-border/30" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
        <path d="M0,0 L200,200 M200,0 L0,200" />
        <rect x="40" y="40" width="120" height="120" />
        <circle cx="100" cy="100" r="5" fill="currentColor" />
      </svg>
      <svg className="pointer-events-none absolute bottom-0 right-0 w-40 md:w-64 h-40 md:h-64 text-border/30 rotate-180" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
        <path d="M0,0 L200,200 M200,0 L0,200" />
        <rect x="40" y="40" width="120" height="120" />
        <circle cx="100" cy="100" r="5" fill="currentColor" />
      </svg>

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <div ref={headerRef} className={`max-w-3xl transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-border" />
          </div>
          <h2 className="font-serif font-default text-3xl md:text-5xl lg:text-6xl leading-[1.0] text-foreground uppercase">
            The Creative{" "}
            <em className="italic font-light text-muted-foreground">Process</em>
          </h2>
          <p className="mt-8 font-sans text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed font-light">
            A disciplined approach to storytelling — from the first script reading to the final cinematic frame.
          </p>
        </div>

        <div ref={timelineRef} className="relative mt-24 md:mt-32">
          
          

          <div className="space-y-12 md:space-y-16">
            {milestones.map((m, i) => {
            const Icon = m.icon;
            return <MilestoneItem key={m.number} data-index={i} number={m.number} timing={m.timing} title={m.title} description={m.description} Icon={Icon} />;
          })}
          </div>
        </div>

        <div ref={footerRef} className={`mt-24 md:mt-32 text-center transition-all duration-1000 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="h-px w-16 bg-border" />
            <div className="size-1 bg-foreground" />
            <span className="h-px w-16 bg-border" />
          </div>
          <p className="font-serif italic text-2xl md:text-4xl text-foreground leading-relaxed max-w-3xl mx-auto">
            From the first mark to the final cut — <br className="hidden md:block" />
            <span className="text-muted-foreground">every frame matters.</span>
          </p>
        </div>
      </div>
    </section>;
}
function MilestoneItem({
  number,
  timing,
  title,
  description,
  Icon,
  ...rest
}: {
  number: string;
  timing: string;
  title: string;
  description: string;
  Icon: React.ComponentType<{
    className?: string;
  }>;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <div ref={ref} className="relative flex flex-col md:flex-row items-start md:items-center gap-8 p-6 md:p-10 bg-card border border-border rounded-lg transition-all duration-500 hover:bg-accent/5 group" {...rest}>
      <div className="flex-shrink-0 z-10">
        <div className={`relative size-4 rounded-md bg-foreground transition-all duration-700 ${isVisible ? "scale-100 opacity-100" : "scale-50 opacity-40"}`}>
          <div className={`absolute inset-0 rounded-md bg-foreground blur-sm transition-opacity duration-700 ${isVisible ? "opacity-40" : "opacity-0"}`} aria-hidden="true" />
        </div>
      </div>

      <div className="flex-grow">
        <ContentBlock number={number} timing={timing} title={title} description={description} Icon={Icon} isVisible={isVisible} />
      </div>
    </div>;
}
function ContentBlock({
  number,
  timing,
  title,
  description,
  Icon,
  isVisible,
  alignRight = false
}: {
  number: string;
  timing: string;
  title: string;
  description: string;
  Icon: React.ComponentType<{
    className?: string;
  }>;
  isVisible: boolean;
  alignRight?: boolean;
}) {
  return <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${alignRight ? "text-right" : "text-left"}`}>
      <div className={`flex items-center gap-3 mb-4 ${alignRight ? "md:justify-end" : "md:justify-start"}`}>
        <Icon className="size-4 text-foreground" />
        <span className="font-mono text-xs tracking-[0.2em] text-muted-foreground uppercase">
          {timing}
        </span>
      </div>
      <div className={`flex items-baseline gap-4 mb-3 ${alignRight ? "md:justify-end" : "md:justify-start"}`}>
        <em className="font-serif font-default italic text-5xl md:text-6xl text-foreground font-light leading-none">
          {number}
        </em>
        <span className="h-px w-8 bg-border" />
      </div>
      <h3 className="font-sans font-default text-2xl md:text-3xl text-foreground mb-4 leading-tight uppercase tracking-tight">
        {title}
      </h3>
      <p className={`font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-full md:max-w-2xl font-light whitespace-normal ${alignRight ? "text-right" : "text-left"}`}>
        {description}
      </p>
    </div>;
}